import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Slider,
  TextField
} from '@mui/material';
import {
  Edit as DrawIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  CheckCircle as ValidIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';

// OpenLayers imports
import Draw from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import Snap from 'ol/interaction/Snap';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Stroke, Fill } from 'ol/style';
import { Polygon } from 'ol/geom';
import Feature from 'ol/Feature';
import { transform } from 'ol/proj';
import {
  PROJECT_GEOMETRY_COLOR_PRESETS,
  PROJECT_GEOMETRY_DEFAULT_COLOR,
  PROJECT_GEOMETRY_INVALID_COLOR,
  normalizeProjectGeometryColor,
} from '../../utils/projectGeometryColors';

interface PolygonDrawingToolProps {
  map: any; // OpenLayers Map instance
  onPolygonComplete: (
    coordinates: number[][],
    metadata: PolygonMetadata,
    isValidationFallback?: boolean,
    color?: string,
    transparency?: number
  ) => void;
  onPolygonModifyComplete?: (data: {
    polygonId: string;
    coordinates: number[][];
    metadata: PolygonMetadata;
    isValidationFallback?: boolean;
  }) => void;
  onPolygonValidation: (coordinates: number[][], isValid: boolean, errors: string[]) => void;
  disabled?: boolean;
  existingPolygons?: PolygonRegion[];
  onPolygonDelete?: (polygonId: string) => void;
}

interface PolygonMetadata {
  area_km2: number;
  perimeter_km: number;
  vertex_count: number;
  bounds: {
    min_lat: number;
    min_lon: number;
    max_lat: number;
    max_lon: number;
  };
}

interface PolygonRegion {
  id: string;
  name: string;
  coordinates: number[][];
  metadata: PolygonMetadata;
  region_type: 'project_boundary' | 'analysis_region';
  is_valid: boolean;
  validation_errors: string[];
  visible: boolean;
  color?: string;
  transparency?: number;
  created_at: string;
  updated_at: string;
}

type DrawingMode = 'none' | 'draw' | 'modify' | 'delete';

const PolygonDrawingTool: React.FC<PolygonDrawingToolProps> = ({
  map,
  onPolygonComplete,
  onPolygonModifyComplete,
  onPolygonValidation,
  disabled = false,
  existingPolygons = [],
  onPolygonDelete
}) => {
  const [drawingMode, setDrawingMode] = useState<DrawingMode>('none');
  const [currentPolygon, setCurrentPolygon] = useState<number[][] | null>(null);
  const [editingPolygonId, setEditingPolygonId] = useState<string | null>(null);
  const [polygonMetadata, setPolygonMetadata] = useState<PolygonMetadata | null>(null);
  const [validationStatus, setValidationStatus] = useState<{
    isValid: boolean;
    errors: string[];
  } | null>(null);
  const [showValidation, setShowValidation] = useState(true);
  const [snapToVertex, setSnapToVertex] = useState(true);
  
  // Color and transparency controls
  const [polygonColor, setPolygonColor] = useState(PROJECT_GEOMETRY_DEFAULT_COLOR);
  const [polygonTransparency, setPolygonTransparency] = useState(0.3); // 30% opacity
  const resolvedPolygonColor = normalizeProjectGeometryColor(polygonColor);

  // OpenLayers interaction refs
  const drawInteractionRef = useRef<Draw | null>(null);
  const modifyInteractionRef = useRef<Modify | null>(null);
  const snapInteractionRef = useRef<Snap | null>(null);
  const vectorLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const vectorSourceRef = useRef<VectorSource | null>(null);

  // Initialize vector layer for polygon drawing
  useEffect(() => {
    if (!map) return;

    // Create vector source and layer for polygons
    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: new Style({
        // Removed fill to avoid interference with grid visualization
        stroke: new Stroke({
          color: resolvedPolygonColor,
          width: 4
        })
      })
    });

    map.addLayer(vectorLayer);
    vectorLayerRef.current = vectorLayer;
    vectorSourceRef.current = vectorSource;

    // Load existing polygons
    loadExistingPolygons();

    return () => {
      if (vectorLayerRef.current) {
        map.removeLayer(vectorLayerRef.current);
      }
      cleanupInteractions();
    };
  }, [map, resolvedPolygonColor, polygonTransparency]);

  // Load existing polygons into the vector layer
  const loadExistingPolygons = useCallback(() => {
    if (!vectorSourceRef.current) return;

    // DEBUG: Log each polygon received
    if (process.env.NODE_ENV === 'development') {
      console.info('[PolygonDrawingTool] existingPolygons', existingPolygons.length);
      const sample = existingPolygons.slice(0, 2).map((polygon) => ({
        id: polygon.id,
        name: polygon.name,
        coordSample: polygon.coordinates.slice(0, 3)
      }));
      console.info('[PolygonDrawingTool] sample', sample);
    }
    
    // CRITICAL FIX: Don't reload existing polygons if user is in the middle of drawing/saving
    if (currentPolygon && polygonMetadata) {
      return;
    }
    
    // Debug: Log all current features before clearing
    const allFeatures = vectorSourceRef.current.getFeatures();
    
    // Clear only existing polygon features, preserve current drawing
    const existingPolygonFeatures = allFeatures.filter(feature => feature.get('polygonId'));
    existingPolygonFeatures.forEach((feature) => {
      vectorSourceRef.current?.removeFeature(feature);
    });
    
    // Debug: Log remaining features after clearing
    const remainingFeatures = vectorSourceRef.current.getFeatures();
    
    existingPolygons.forEach(polygon => {
      // Convert coordinates to OpenLayers format (EPSG:3857)
      const transformedCoords = polygon.coordinates.map((coord: number[]) => 
        transform([coord[0], coord[1]], 'EPSG:4326', 'EPSG:3857')
      );
      
      // Ensure polygon is closed
      if (transformedCoords[0][0] !== transformedCoords[transformedCoords.length - 1][0] ||
          transformedCoords[0][1] !== transformedCoords[transformedCoords.length - 1][1]) {
        transformedCoords.push(transformedCoords[0]);
      }

      const geometry = new Polygon([transformedCoords]);
      const feature = new Feature({
        geometry,
        polygonId: polygon.id,
        polygonType: polygon.region_type,
        isValid: polygon.is_valid
      });

      // Keep all project/analysis geometry in the cool boundary palette, including
      // legacy saved regions that still carry a forbidden warm/purple color.
      const baseColor = normalizeProjectGeometryColor(
        polygon.color,
        polygon.is_valid ? PROJECT_GEOMETRY_DEFAULT_COLOR : PROJECT_GEOMETRY_INVALID_COLOR
      );
      feature.setStyle(new Style({
        // Add a nearly transparent fill so the polygon remains clickable without extra visuals
        fill: new Fill({
          color: 'rgba(0, 0, 0, 0.01)'
        }),
        stroke: new Stroke({
          color: baseColor,
          width: polygon.region_type === 'project_boundary' ? 4 : 3
        })
      }));
      vectorSourceRef.current?.addFeature(feature);
    });
  }, [existingPolygons, currentPolygon, polygonMetadata]);

  // Update existing polygons when they change
  const loadingRef = useRef(false);

  useEffect(() => {
    // Prevent simultaneous loads
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    loadExistingPolygons();
    loadingRef.current = false;
  }, [existingPolygons, loadExistingPolygons]);

  // Clear current polygon state when drawing is disabled (polygon saved or cancelled)
  useEffect(() => {
    if (disabled) {
      setCurrentPolygon(null);
      setPolygonMetadata(null);
      setValidationStatus(null);
       setEditingPolygonId(null);
      // Also clear any drawn features that don't have polygonId (current drawing)
      if (vectorSourceRef.current) {
        const features = vectorSourceRef.current.getFeatures();
        const currentDrawingFeatures = features.filter(feature => !feature.get('polygonId'));
        currentDrawingFeatures.forEach(feature => {
          vectorSourceRef.current?.removeFeature(feature);
        });
      }
    }
  }, [disabled]);

  // Cleanup interactions
  const cleanupInteractions = useCallback(() => {
    if (drawInteractionRef.current) {
      map?.removeInteraction(drawInteractionRef.current);
      drawInteractionRef.current = null;
    }
    if (modifyInteractionRef.current) {
      map?.removeInteraction(modifyInteractionRef.current);
      modifyInteractionRef.current = null;
    }
    if (snapInteractionRef.current) {
      map?.removeInteraction(snapInteractionRef.current);
      snapInteractionRef.current = null;
    }
  }, [map]);

  // Start drawing mode
  const startDrawing = useCallback(() => {
    if (!map || !vectorSourceRef.current) return;

    cleanupInteractions();

    const drawInteraction = new Draw({
      source: vectorSourceRef.current,
      type: 'Polygon',
      style: new Style({
        // Removed fill to avoid interference with grid visualization
        stroke: new Stroke({
          color: resolvedPolygonColor,
          width: 3
        })
      })
    });

    drawInteraction.on('drawend', handleDrawEnd);
    map.addInteraction(drawInteraction);
    drawInteractionRef.current = drawInteraction;

    // Add snap interaction if enabled
    if (snapToVertex) {
      const snapInteraction = new Snap({
        source: vectorSourceRef.current
      });
      map.addInteraction(snapInteraction);
      snapInteractionRef.current = snapInteraction;
    }

    setDrawingMode('draw');
  }, [map, snapToVertex, resolvedPolygonColor, polygonTransparency]);

  // Handle draw completion
  const handleDrawEnd = useCallback(async (event: any) => {
    const feature = event.feature;
    const geometry = feature.getGeometry();
    
    // Debug: Check current features after drawing
    vectorSourceRef.current?.getFeatures().forEach((f, index) => {
      });
    
    if (geometry instanceof Polygon) {
      // Get coordinates in EPSG:4326 (lon, lat)
      const coordinates = geometry.getCoordinates()[0].map((coord: number[]) => {
        const transformed = transform(coord, 'EPSG:3857', 'EPSG:4326');
        return [transformed[0], transformed[1]]; // [lon, lat]
      });

      // Remove the last coordinate if it's a duplicate of the first (closing point)
      if (coordinates.length > 1) {
        const first = coordinates[0];
        const last = coordinates[coordinates.length - 1];
        if (Math.abs(first[0] - last[0]) < 1e-10 && Math.abs(first[1] - last[1]) < 1e-10) {
          coordinates.pop();
        }
      }

      setCurrentPolygon(coordinates);
      
      // Validate polygon with backend
      await validatePolygon(coordinates);
    }
  }, []);

  // Validate polygon with backend API
  const validatePolygon = useCallback(async (coordinates: number[][]) => {
    try {
      const response = await fetch('http://localhost:8000/api/polygon/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ coordinates }),
      });

      const result = await response.json();
      if (result.valid) {
        const metadata: PolygonMetadata = {
          area_km2: result.area_km2,
          perimeter_km: result.perimeter_km,
          vertex_count: result.vertex_count,
          bounds: result.bounds
        };
        
        setPolygonMetadata(metadata);
        setValidationStatus({ isValid: true, errors: [] });
        onPolygonValidation(coordinates, true, []);
      } else {
        setValidationStatus({ isValid: false, errors: result.errors || [] });
        onPolygonValidation(coordinates, false, result.errors || []);
      }
    } catch (error) {
      console.error('Polygon validation failed:', error);
      
      // Calculate real area and perimeter using geographic formulas
      const bounds = {
        min_lat: Math.min(...coordinates.map(c => c[1])),
        min_lon: Math.min(...coordinates.map(c => c[0])),
        max_lat: Math.max(...coordinates.map(c => c[1])),
        max_lon: Math.max(...coordinates.map(c => c[0]))
      };
      
      // Calculate approximate area using bounding box (rough estimate)
      const latDiff = bounds.max_lat - bounds.min_lat;
      const lonDiff = bounds.max_lon - bounds.min_lon;
      const avgLat = (bounds.max_lat + bounds.min_lat) / 2;
      
      // Convert degrees to km (rough approximation)
      const kmPerDegreeLat = 111.32; // km per degree latitude
      const kmPerDegreeLon = 111.32 * Math.cos(avgLat * Math.PI / 180); // km per degree longitude
      
      const roughAreaKm2 = (latDiff * kmPerDegreeLat) * (lonDiff * kmPerDegreeLon);
      const roughPerimeterKm = 2 * ((latDiff * kmPerDegreeLat) + (lonDiff * kmPerDegreeLon));
      
      // Create realistic metadata based on actual coordinates
      const fallbackMetadata: PolygonMetadata = {
        area_km2: Math.round(roughAreaKm2 * 100) / 100, // Round to 2 decimal places
        perimeter_km: Math.round(roughPerimeterKm * 100) / 100, // Round to 2 decimal places
        vertex_count: coordinates.length,
        bounds: bounds
      };
      
      console.info('📊 Area calculated from bounding box approximation - will be refined by backend');
      
      setPolygonMetadata(fallbackMetadata);
      setValidationStatus({ 
        isValid: false, 
        errors: ['Failed to validate polygon with server - using approximate calculations'] 
      });
      onPolygonValidation(coordinates, false, ['Failed to validate polygon with server - using approximate calculations']);
    }
  }, [onPolygonValidation]);

  // Handle modify completion
  const handleModifyEnd = useCallback(
    async (event: any) => {
      const features = event.features.getArray();
      if (!features.length) {
        return;
      }

      const feature = features[0];
      const geometry = feature.getGeometry();

      if (!(geometry instanceof Polygon)) {
        return;
      }

      const polygonId = feature.get('polygonId') || null;
      setEditingPolygonId(polygonId);

      const coordinates = geometry.getCoordinates()[0].map((coord: number[]) => {
        const transformed = transform(coord, 'EPSG:3857', 'EPSG:4326');
        return [transformed[0], transformed[1]];
      });

      // Remove duplicate closing point
      if (coordinates.length > 1) {
        coordinates.pop();
      }

      setCurrentPolygon(coordinates);
      await validatePolygon(coordinates);
    },
    [validatePolygon]
  );

  // Start modify mode
  const startModifying = useCallback(() => {
    if (!map || !vectorSourceRef.current) return;

    cleanupInteractions();

    const modifyInteraction = new Modify({
      source: vectorSourceRef.current
    });

    modifyInteraction.on('modifystart', (event: any) => {
      const features = event.features.getArray();
      if (!features.length) {
        setEditingPolygonId(null);
        return;
      }

      const feature = features[0];
      const polygonId = feature.get('polygonId') || null;
      setEditingPolygonId(polygonId);
    });

    modifyInteraction.on('modifyend', handleModifyEnd);
    map.addInteraction(modifyInteraction);
    modifyInteractionRef.current = modifyInteraction;

    setDrawingMode('modify');
  }, [map, cleanupInteractions, handleModifyEnd]);

  // Stop drawing mode
  const stopDrawing = useCallback(() => {
    cleanupInteractions();
    setDrawingMode('none');
    setEditingPolygonId(null);
  }, [cleanupInteractions]);

  // Save current polygon
  const savePolygon = useCallback(() => {
    if (!currentPolygon || !polygonMetadata) {
      return;
    }

    const isValidationFallback = validationStatus ? !validationStatus.isValid : false;

    if (editingPolygonId && onPolygonModifyComplete) {
      onPolygonModifyComplete({
        polygonId: editingPolygonId,
        coordinates: currentPolygon,
        metadata: polygonMetadata,
        isValidationFallback
      });
      setCurrentPolygon(null);
      setPolygonMetadata(null);
      setValidationStatus(null);
      setEditingPolygonId(null);
      stopDrawing();
      return;
    }

    onPolygonComplete(
      currentPolygon,
      polygonMetadata,
      isValidationFallback,
      resolvedPolygonColor,
      polygonTransparency
    );
  }, [
    currentPolygon,
    polygonMetadata,
    validationStatus,
    editingPolygonId,
    onPolygonModifyComplete,
    stopDrawing,
    onPolygonComplete,
    resolvedPolygonColor,
    polygonTransparency
  ]);

  // Clear current drawing
  const clearDrawing = useCallback(() => {
    if (vectorSourceRef.current) {
      // Only clear the currently drawn polygon, not existing ones
      const features = vectorSourceRef.current.getFeatures();
      const newFeatures = features.filter(feature => !feature.get('polygonId'));
      newFeatures.forEach(feature => vectorSourceRef.current?.removeFeature(feature));
    }
    
    setCurrentPolygon(null);
    setPolygonMetadata(null);
    setValidationStatus(null);
    setEditingPolygonId(null);
  }, []);

  // Delete polygon by clicking
  const startDeleting = useCallback(() => {
    if (!map) return;
    
    cleanupInteractions();
    setDrawingMode('delete');
    
    // Add click handler for deletion
    const handleClick = (event: any) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (feature: any) => feature);
      if (feature && feature.get('polygonId')) {
        const polygonId = feature.get('polygonId');
        if (onPolygonDelete) {
          onPolygonDelete(polygonId);
        }
        vectorSourceRef.current?.removeFeature(feature);
      }
    };
    
    map.on('click', handleClick);
    
    // Store the handler for cleanup
    (map as any)._polygonDeleteHandler = handleClick;
  }, [map, onPolygonDelete]);

  // Cleanup delete mode
  const stopDeleting = useCallback(() => {
    if (map && (map as any)._polygonDeleteHandler) {
      map.un('click', (map as any)._polygonDeleteHandler);
      delete (map as any)._polygonDeleteHandler;
    }
    setDrawingMode('none');
  }, [map]);

  // Format area for display
  const formatArea = (area_km2: number): string => {
    if (area_km2 >= 1000) {
      return `${(area_km2 / 1000).toFixed(1)}k km²`;
    }
    return `${area_km2.toFixed(1)} km²`;
  };

  // Format perimeter for display
  const formatPerimeter = (perimeter_km: number): string => {
    if (perimeter_km >= 1000) {
      return `${(perimeter_km / 1000).toFixed(1)}k km`;
    }
    return `${perimeter_km.toFixed(1)} km`;
  };

  return (
    <Box 
      className="palantir-panel"
      sx={{ 
        position: 'absolute', 
        top: 16, 
        right: 16, 
        minWidth: 320,
        maxWidth: 400,
        zIndex: 1000,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        display: disabled ? 'none' : 'block'
      }}
    >
      <Box className="palantir-panel-header">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography 
            sx={{
              color: 'var(--palantir-text-primary)',
              fontFamily: 'var(--palantir-font-mono)',
              fontSize: 'var(--palantir-text-sm)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Polygon Analysis
          </Typography>
          <Box display="flex" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={showValidation}
                  onChange={(e) => setShowValidation(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography sx={{
                  fontSize: 'var(--palantir-text-xs)',
                  color: 'var(--palantir-text-muted)',
                  fontFamily: 'var(--palantir-font-mono)'
                }}>
                  Validation
                </Typography>
              }
            />
          </Box>
        </Box>
      </Box>

      <Box className="palantir-panel-content">

      {/* Drawing Controls - Only show when drawing is enabled */}
      {!disabled && (
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Tooltip title="Draw Polygon">
          <IconButton
            onClick={drawingMode === 'draw' ? stopDrawing : startDrawing}
            color={drawingMode === 'draw' ? 'primary' : 'default'}
            size="small"
          >
            {drawingMode === 'draw' ? <StopIcon /> : <DrawIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Modify Polygon">
          <span>
            <IconButton
              onClick={drawingMode === 'modify' ? stopDrawing : startModifying}
              color={drawingMode === 'modify' ? 'primary' : 'default'}
              size="small"
              disabled={existingPolygons.length === 0 && !currentPolygon}
            >
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Delete Polygon">
          <span>
            <IconButton
              onClick={drawingMode === 'delete' ? stopDeleting : startDeleting}
              color={drawingMode === 'delete' ? 'error' : 'default'}
              size="small"
              disabled={existingPolygons.length === 0}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Clear Current Drawing">
          <span>
            <IconButton
              onClick={clearDrawing}
              size="small"
              disabled={!currentPolygon}
            >
              <ClearIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Undo Last Point">
          <span>
            <IconButton
              onClick={() => {
                if (drawInteractionRef.current) {
                  drawInteractionRef.current.removeLastPoint();
                }
              }}
              size="small"
              disabled={drawingMode !== 'draw'}
            >
              <UndoIcon />
            </IconButton>
          </span>
        </Tooltip>
        </Box>
      )}

      {/* Settings - Only show when drawing is enabled */}
      {!disabled && (
        <Box mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={snapToVertex}
                onChange={(e) => setSnapToVertex(e.target.checked)}
                size="small"
              />
            }
            label="Snap to vertices"
          />
          
          {/* Color and Transparency Controls */}
          <Box mt={2}>
            <Typography 
              sx={{
                color: 'var(--palantir-text-secondary)',
                fontFamily: 'var(--palantir-font-mono)',
                fontSize: 'var(--palantir-text-xs)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <PaletteIcon sx={{ fontSize: 14 }} />
              Polygon Style
            </Typography>
            
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mb={1}>
              <Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'var(--palantir-text-muted)', 
                    fontFamily: 'var(--palantir-font-mono)',
                    fontSize: '10px'
                  }}
                >
                  Color
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: resolvedPolygonColor,
                      border: '2px solid var(--palantir-border-default)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'color';
                      input.value = resolvedPolygonColor;
                      input.onchange = (e) => setPolygonColor(
                        normalizeProjectGeometryColor((e.target as HTMLInputElement).value)
                      );
                      input.click();
                    }}
                  >
                    <input
                      type="color"
                      value={resolvedPolygonColor}
                      onChange={(e) => setPolygonColor(normalizeProjectGeometryColor(e.target.value))}
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                  </Box>
                  <TextField
                    value={polygonColor}
                    onChange={(e) => setPolygonColor(e.target.value)}
                    size="small"
                    sx={{ 
                      '& .MuiInputBase-input': {
                        fontSize: '10px',
                        fontFamily: 'var(--palantir-font-mono)',
                        color: 'var(--palantir-text-primary)',
                        padding: '4px 8px'
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'var(--palantir-border-default)' }
                      }
                    }}
                  />
                </Box>
              </Box>
              
              <Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'var(--palantir-text-muted)', 
                    fontFamily: 'var(--palantir-font-mono)',
                    fontSize: '10px'
                  }}
                >
                  Transparency: {Math.round(polygonTransparency * 100)}%
                </Typography>
                <Slider
                  value={polygonTransparency}
                  onChange={(_, value) => setPolygonTransparency(value as number)}
                  min={0.1}
                  max={0.8}
                  step={0.1}
                  size="small"
                  sx={{
                    mt: 0.5,
                    color: 'var(--palantir-blue-500)',
                    '& .MuiSlider-thumb': {
                      backgroundColor: 'var(--palantir-blue-500)',
                      border: '2px solid var(--palantir-bg-primary)'
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: 'var(--palantir-blue-500)'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'var(--palantir-border-subtle)'
                    }
                  }}
                />
              </Box>
            </Box>
            
            {/* Color Presets */}
            <Box display="flex" gap={0.5} mt={1}>
              {PROJECT_GEOMETRY_COLOR_PRESETS.map((preset) => (
                <Tooltip key={preset.name} title={preset.name}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: preset.color,
                      border: resolvedPolygonColor === preset.color ? '2px solid var(--palantir-text-primary)' : '1px solid var(--palantir-border-subtle)',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      '&:hover': {
                        border: '2px solid var(--palantir-text-secondary)'
                      }
                    }}
                    onClick={() => setPolygonColor(preset.color)}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* Current Polygon Info */}
      {currentPolygon && polygonMetadata && (
        <>
          <Divider sx={{ borderColor: 'var(--palantir-border-subtle)', my: 2 }} />
          <Typography 
            sx={{
              color: 'var(--palantir-blue-400)',
              fontFamily: 'var(--palantir-font-mono)',
              fontSize: 'var(--palantir-text-sm)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 2
            }}
          >
            Active Region
          </Typography>
          
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mb={2}>
            <Box className="metric-card">
              <Typography className="metric-label">Area</Typography>
              <Typography className="metric-value">{formatArea(polygonMetadata.area_km2)}</Typography>
            </Box>
            <Box className="metric-card">
              <Typography className="metric-label">Perimeter</Typography>
              <Typography className="metric-value">{formatPerimeter(polygonMetadata.perimeter_km)}</Typography>
            </Box>
          </Box>
          
          <Box className="metric-card" sx={{ mb: 2 }}>
            <Typography className="metric-label">Vertices</Typography>
            <Typography className="metric-value">{polygonMetadata.vertex_count}</Typography>
          </Box>

          {/* Validation Status */}
          {showValidation && validationStatus && (
            <Box 
              className={`status-indicator ${validationStatus.isValid ? 'operational' : 'failed'}`}
              sx={{ mb: 2, justifyContent: 'flex-start' }}
            >
              {validationStatus.isValid ? <ValidIcon /> : <WarningIcon />}
              <Box>
                <Typography sx={{
                  fontFamily: 'var(--palantir-font-mono)',
                  fontSize: 'var(--palantir-text-xs)',
                  fontWeight: 600
                }}>
                  {validationStatus.isValid ? 'VALIDATED' : 'VALIDATION FAILED'}
                </Typography>
                {!validationStatus.isValid && (
                  <Box sx={{ mt: 0.5 }}>
                    {validationStatus.errors.map((error, index) => (
                      <Typography 
                        key={index}
                        sx={{
                          fontSize: '10px',
                          color: 'var(--palantir-text-muted)',
                          fontFamily: 'var(--palantir-font-mono)',
                          lineHeight: 1.3
                        }}
                      >
                        • {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Save Button */}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={savePolygon}
            disabled={false}
            fullWidth
            size="small"
          >
            Save Polygon
          </Button>
        </>
      )}

      {/* Drawing Instructions */}
      {drawingMode === 'draw' && (
        <Box 
          className="status-indicator processing"
          sx={{ mt: 2, justifyContent: 'flex-start' }}
        >
          <InfoIcon />
          <Typography sx={{
            fontFamily: 'var(--palantir-font-mono)',
            fontSize: 'var(--palantir-text-xs)'
          }}>
            Click to add vertices. Double-click to complete polygon.
          </Typography>
        </Box>
      )}

      {drawingMode === 'modify' && (
        <Box 
          className="status-indicator processing"
          sx={{ mt: 2, justifyContent: 'flex-start' }}
        >
          <InfoIcon />
          <Typography sx={{
            fontFamily: 'var(--palantir-font-mono)',
            fontSize: 'var(--palantir-text-xs)'
          }}>
            Drag vertices to modify polygon shape.
          </Typography>
        </Box>
      )}

      {drawingMode === 'delete' && (
        <Box 
          className="status-indicator degraded"
          sx={{ mt: 2, justifyContent: 'flex-start' }}
        >
          <WarningIcon />
          <Typography sx={{
            fontFamily: 'var(--palantir-font-mono)',
            fontSize: 'var(--palantir-text-xs)'
          }}>
            Click on a polygon to delete it.
          </Typography>
        </Box>
      )}

      {/* Existing Polygons Summary */}
      {existingPolygons.length > 0 && (
        <>
          <Divider sx={{ borderColor: 'var(--palantir-border-subtle)', my: 2 }} />
          <Typography 
            sx={{
              color: 'var(--palantir-blue-400)',
              fontFamily: 'var(--palantir-font-mono)',
              fontSize: 'var(--palantir-text-sm)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 2
            }}
          >
            Existing Regions ({existingPolygons.length})
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {existingPolygons.map((polygon) => (
              <Chip
                key={polygon.id}
                label={`${polygon.name} (${formatArea(polygon.metadata.area_km2)})`}
                size="small"
                className={`status-indicator ${polygon.is_valid ? 'operational' : 'failed'}`}
                sx={{
                  fontSize: '10px',
                  fontFamily: 'var(--palantir-font-mono)',
                  fontWeight: 500
                }}
              />
            ))}
          </Box>
        </>
      )}
      
      </Box> {/* Close palantir-panel-content */}
    </Box>
  );
};

export default PolygonDrawingTool;
